import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { createForm } from 'final-form';
import { Form } from 'react-final-form';

import OrderSummary from './OrderSummary';
import Summary from './Summary';
import LegalInfo from '../components/legalInfo/LegalInfo';
import CouponCode from '../components/couponCode';
import EditDetailsForms from './EditDetailsForms';
import NewPaymentDetails from './NewPaymentDetails';
import { CheckoutForm, SubmitButton } from '../helpers/Styled';
import setFieldTouched from '../helpers/setFieldTouched';
import { css } from '@emotion/react';
import { observer } from 'mobx-react';

const StyledFormDiv = styled.div(({ theme }) => css`
	width: 100%;
	display: flex;
	flex-direction: column;
	${theme.mq.mega} {
		flex-direction: row;
		align-items: flex-start;
	}
`);

const Fieldset = styled.fieldset`
	border: 0;
	margin: 0;
	padding: 0;
`;

const PurchaseSubscription = observer(({
	onSubmit,
	legalInfo,
	businessAccount,
	customer,
	planId,
	config,
	showCardZipCode,
	showBillingDetails,
	showVatNumber,
	legalEntity,
	showCoupon,
	widgetOffset,
	isMobile,
	onPaymentDetailsSubmit,
	onVatNumberSubmit,
	onBillingDetailsSubmit,
	promotionId,
	discountId,
}) => {
	const { t } = useTranslation();
	const { coupon, invalidCouponCode, isLoading, couponCode, planName, billingDetails, isPersistedPaymentMethodAvailable } = customer;
	const form = createForm({ mutators: { setFieldTouched }, onSubmit });

	return (
		<Form form={form}>
			{({ submitting, validating, handleSubmit }) => (
				<StyledFormDiv>
					<CheckoutForm as="section">
						<Fieldset disabled={submitting}>
							{isPersistedPaymentMethodAvailable ?
								<EditDetailsForms
									variant="update"
									customer={customer}
									config={config}
									showCardZipCode={showCardZipCode}
									showBillingDetails={showBillingDetails}
									showVatNumber={showVatNumber}
									legalEntity={legalEntity}
									onPaymentDetailsSubmit={onPaymentDetailsSubmit}
									onVatNumberSubmit={onVatNumberSubmit}
									onBillingDetailsSubmit={onBillingDetailsSubmit}
								/> :
								<NewPaymentDetails
									customer={customer}
									businessAccount={businessAccount}
									legalEntity={legalEntity}
									showCardZipCode={showCardZipCode}
									showBillingDetails={showBillingDetails}
									showVatNumber={showVatNumber}
								/>}

						</Fieldset>
						{!isMobile && (

							<>
								{legalInfo && <LegalInfo source={legalInfo} />}
								<SubmitButton
									isLoading={submitting || validating}
									variant="primary"
									loadingLabel="loading"
									onClick={handleSubmit}
								>
									{t('purchase_subscription.title')}
								</SubmitButton>
							</>

						)}
					</CheckoutForm>
					<OrderSummary
						as="section"
						isMobile={isMobile}
						widgetOffset={widgetOffset}
					>
						<>
							<Summary
								config={config}
								planId={planId}
								country={billingDetails.country}
								vatNumber={billingDetails.vatNumber}
								isMobile={isMobile}
								couponCode={couponCode}
								showCoupon={showCoupon}
								customer={customer}
								promotionId={promotionId}
								discountId={discountId}
							/>
							{showCoupon && <CouponCode
								couponCode={couponCode}
								coupon={coupon}
								planName={planName}
								invalidCouponCode={invalidCouponCode}
								onChange={(couponCode) => customer.setCouponCode({ couponCode, planId })}
								isLoading={isLoading}
							/>}
						</>
						{isMobile && (
							<>
								{legalInfo && <LegalInfo source={legalInfo} isMobile />}
								<SubmitButton
									isLoading={submitting || validating}
									loadingLabel="loading"
									variant="primary"
									stretch
									isMobile
									onClick={handleSubmit}
								>
									{t('purchase_subscription.title')}
								</SubmitButton>
							</>
						)}
					</OrderSummary>
				</StyledFormDiv>
			)}
		</Form>
	);
});

PurchaseSubscription.propTypes = {
	onSubmit: PropTypes.func.isRequired,
	planId: PropTypes.string.isRequired,
	legalInfo: PropTypes.string,
	showBillingDetails: PropTypes.bool,
	showVatNumber: PropTypes.bool,
	customer: PropTypes.shape({
		setPaymentMethod: PropTypes.func.isRequired,
		setVatNumber: PropTypes.func.isRequired,
		billingDetails: PropTypes.object.isRequired,
	}).isRequired,
	config: PropTypes.shape({
		sumupService: PropTypes.object.isRequired,
		epayService: PropTypes.object
	}).isRequired,
};

export default PurchaseSubscription;
